import React from 'react';
import type { FamilyMember } from '../../types/family';

interface MemberPickerProps {
  /** All members available to pick from */
  members: FamilyMember[];
  /** Currently selected member id (or null) */
  value: string | null;
  /** Called when a member is picked or cleared (null) */
  onChange: (memberId: string | null) => void;
  /** Optional label above the picker */
  label?: string;
  /** Placeholder text when nothing is selected */
  placeholder?: string;
}

/**
 * Searchable dropdown for picking an existing family member.
 * Used by the public submission form and the admin review view.
 */
export const MemberPicker: React.FC<MemberPickerProps> = ({
  members,
  value,
  onChange,
  label,
  placeholder = 'Search a member…',
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const selected = members.find(m => m.id === value) ?? null;

  const filtered = query.trim()
    ? members.filter(m => m.name.toLowerCase().includes(query.trim().toLowerCase()))
    : members;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-left text-sm focus:outline-none focus:ring-2 focus:ring-green-600 bg-white flex items-center justify-between"
        >
          <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
            {selected ? selected.name : placeholder}
          </span>
          <span className="text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
        </button>

        {open && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
            <input
              autoFocus
              type="text"
              placeholder="Search…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full px-3 py-2 border-b border-gray-100 text-sm focus:outline-none"
            />
            <div className="py-1">
              {selected && (
                <button
                  type="button"
                  onClick={() => { onChange(null); setOpen(false); setQuery(''); }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  ✕ Clear selection
                </button>
              )}
              {filtered.length === 0 && (
                <p className="px-4 py-2 text-sm text-gray-400">No members found</p>
              )}
              {filtered.map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { onChange(m.id); setOpen(false); setQuery(''); }}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-green-50 ${
                    m.id === value ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-800'
                  }`}
                >
                  {m.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
